import { faClock, faSearch, faShield } from '@fortawesome/pro-light-svg-icons';
import React from 'react';
import { useHistory } from 'react-router';
import { slugs, useRentalSteps } from '../../app/steps';
import { PageContainer } from 'shared/components/PageContainer';
import { RentalPageTitleHelmet } from 'rental/components/PageTitleHelmet';
import { PhoneNumberLink } from 'shared/components/PhoneNumber';
import { ButtonProps } from 'shared/components/props';
import { ReactBootstrapModal } from 'shared/components/ReactModal';
import { CentreButton, NextButton, PrevLink, SubmitButtons } from 'shared/components/SubmitButtons';
import { PageHeading } from 'shared/components/typography';
import { BankStatementsResult, useBankStatementsQuery, useSkipBankStatementsMutation } from '../../generated.graphql';
import { RentalStepProps } from 'rental/app/steps';
import { BankStatementsFeature } from './BankStatementsFeature';
import { useBankStatements } from './hooks';

interface Props extends RentalStepProps {
  application: {portal: {slug: string}}
}


export const BankStatements = (props: Props) => {
  const steps = useRentalSteps({slug: slugs.bankStatements});
  const history = useHistory();
  const [result] = useBankStatementsQuery({variables: {portalSlug: props.application.portal.slug}})
  const [skipMutationResult, executeSkipMutation] = useSkipBankStatementsMutation();
  const portalSlug = props.application.portal.slug
  const handleSkip = async () => {
    const r = await executeSkipMutation({
      input: { portalSlug },
    });
    if (r.data?.skipBankStatements?.application?.bankStatements?.result === BankStatementsResult.Skip && steps.next) {
      history.push(steps.next.link);
    }
  };
  const [retry, setRetry] = React.useState(false);
  const [isOpen, setIsOpen] = React.useState(false);
  const bs = result.data?.portal?.myApplication?.bankStatements;

  useBankStatements({
    portalSlug: props.application.portal.slug,
    onComplete: r => {
      setIsOpen(false);
      if (steps.next && r === "COMPLETE") {
        history.push(steps.next?.link);
      }
      else {
        setRetry(false)
      }
    },
    reference: result.data?.portal?.myApplication?.bankStatements.reference || "",
  });
  
  // Disable buttons while mutating
  const isBusy = skipMutationResult.fetching;

  let { result: value, errorCode=null, errorMessage=null } = result.data?.portal?.myApplication?.bankStatements || {};

  const showExplanation = (!value || retry) && bs?.embedURL;

  const RetryAlert = (p: {children: React.ReactNode, type: "warning" | "success" | "secondary", btnText: React.ReactNode}) => (
    <div className={`alert alert-${p.type} d-sm-flex align-items-center mb-5`}>
      <div className="mr-auto mb-3 mb-sm-0">
        {p.children}
      </div>
      <button className={`btn btn-outline-secondary ml-sm-5`} type="button" onClick={() => setRetry(!retry)}>
        {p.btnText}
      </button>
    </div>
  )

  return (
    <div>
      <PageContainer flushBottom>
        <RentalPageTitleHelmet>Bank Statements</RentalPageTitleHelmet>
        <PageHeading>Bank Statements</PageHeading>
      </PageContainer>

      <PageContainer flushTop flushBottom>
        {value === BankStatementsResult.Complete && (
          <RetryAlert type="secondary" btnText={retry ? <>Use&nbsp;these</> : <>Provide&nbsp;again</>}>
            You have already successfully provided bank statements.
          </RetryAlert>
        )}
      </PageContainer>

      {!showExplanation && (
        <PageContainer flushTop>
          {value === BankStatementsResult.Skip && (
            <RetryAlert type="warning" btnText={<>Provide&nbsp;now</>}>
              You have chosen to not provide bank statements.  
              We may contact you later to organise proof of income.
            </RetryAlert>
          )}
          {((value === BankStatementsResult.Error) || (value === BankStatementsResult.LoginFail)) && 
            <RetryAlert type="warning" btnText={<>Try&nbsp;again</>}>
              An issue was encountered providing bank statements.  
              You can skip to the next step but we may need to contact you later for proof of income.
              {(errorCode || errorMessage) && 
                <b>
                  <br/>
                  <br/>
                  {errorCode && <>Error code {errorCode}: </>}
                  {errorMessage}
                </b>
              }
            </RetryAlert>
          }
          <SubmitButtons
            left={steps.prev && <PrevLink to={steps.prev?.link} disabled={isBusy}/>}
            right={<NextButton onClick={() => props.onSubmitSuccessful()} disabled={isBusy}/>}
          />
        </PageContainer>
      )}
      {showExplanation && (
        <>
          <BankStatementsExplanation/>

          <PageContainer>
            <SubmitButtons
              left={steps.prev && <PrevLink to={steps.prev?.link} disabled={isBusy}/>}
              centre={<CentreButton onClick={handleSkip} disabled={isBusy}>Skip this step</CentreButton>}
              right={<NextButton onClick={() => setIsOpen(true)} disabled={isBusy}/>}
            />
          </PageContainer>

          <ReactBootstrapModal
            title="Provide Bank Statements"
            isOpen={isOpen}
            onRequestClose={() => setIsOpen(false)}
            bodyContainerClass="h-100"
          >
            <iframe src={bs?.embedURL} className="w-100 h-100 border-0" title="Provide Bank Statements"></iframe>
          </ReactBootstrapModal>
        </>
      )}
    </div>
  )
};

// const BankStatementsHeading = () => (
//   <>
//     <PageTitleHelmet>Bank Statements</PageTitleHelmet>
//     <PageHeading>Bank Statements</PageHeading>
//   </>
// );

const BankStatementsExplanation = () => (
  <>
    <PageContainer flushTop>
      {/* <BankStatementsHeading/> */}
      <p className="">

        Almost there! We just need to verify your income. 
        Under Australia's responsible lending guidelines, we are required to assess your ability to 
        meet the monthly rental payments for the products you have chosen. 
        <br className="d-inline d-sm-none" />
        <br className="d-inline d-sm-none" />
        We do this by retrieving a read only copy of your most recent bank statements using a fast a secure service. 

      </p>
      <p className="" style={{ fontSize: '20px', fontWeight: 700 }}>It’s safe and easy</p>
      <br />
    </PageContainer>
    <div>
      <div className="bg-white" style={{boxShadow: '0 0 20px rgba(0,0,0,0.2)'}}>
        <PageContainer>
          <div className="row py-3">
            <BankStatementsFeature icon={faShield} title="Secure encryption" className="col-md-4">
              The transfer of your information is encrypted with SSL technology,{' '}
              <br className="d-sm-none" />
              we never see or store your login details.
            </BankStatementsFeature>

            <BankStatementsFeature icon={faClock} title="Faster decision" className="col-md-4">
              It only takes a few seconds to link, which means you can get a faster decision.
            </BankStatementsFeature>

            <BankStatementsFeature icon={faSearch} title="Read only information" className="col-md-4">
              We get read only access to 90 days of your bank statements.
            </BankStatementsFeature>
          </div>
        </PageContainer>
      </div>
      <br />
      <PageContainer extraClassName="text-center">
        <BankStatementsExplanationModalButton className="btn btn-link">
          Learn more about how this works
        </BankStatementsExplanationModalButton>
        {/* <p className="text-center">
          If you have any more questions, you can read more about
          <a href="#" data-toggle="modal" data-target="#aboutBankStatementsModal">how this works</a>.
        </p>
        <p className="text-center">
          If you don’t want to provide us with read only access to your bank statements you can skip this step by
          <button className="btn btn-link" data-skip-btn id="_gonext" style={{ padding: 0, lineHeight: 'inherit', display: 'inline', margin: 0, border: 'none', verticalAlign: 'inherit' }}>clicking here</button>.
          This will require us to contact you, and can take up to 3 business days to assess your application.
        </p> */}
      </PageContainer>
    </div>
  </>
);


const BankStatementsExplanationModalButton = (props: ButtonProps) => {
  const [isOpen, setIsOpen] = React.useState(false);
  return (
    <>
      <button type="button" onClick={() => setIsOpen(true)} {...props}/>
      <ReactBootstrapModal 
        isOpen={isOpen} 
        title="Supporting Information – How it Works"
        onRequestClose={() => setIsOpen(false)}
      >
        <p className="mt-3">
          <b>How does this work?</b><br />
          We need to verify your income in order to process and approve your application. We do this by checking your most recent bank statements.
        </p>
        <p>
          <b>Why am I being asked for my bank login information?</b><br />
          Providing your login information allows us to process your application much faster. Instead of having to manually print, scan and email your bank statements or pay slips to us, providing read only access to your bank statements allows us to access your information instantly.
        </p>
        <p>
          <b>Will you log in to my account?</b><br />
          No. When you submit your login details, a secure third party provider (illion Australia Pty Ltd ) will access read only copies of your bank statements. This is done instantly and securely. We will not have access to your account or login details and we will only see the information we need to approve your application.
        </p>
        <p>
          <b>Do you get access to my account?</b><br />
          No. We don't get access to your account, we get read only access to your bank statements. We can’t login or make any changes.
        </p>
        <p>
          <b>It this safe? Why use a third party provider?</b><br />
          Yes. We use a third party provider to ensure your data is secured by 256 bit bank level encryption. Illion is owned and operated in Australia.
        </p>
        <p className="mb-5">
          <b>Contact Us</b><br />
          If you have any concerns or require any further information, please do not hesitate to call our free helpline on <PhoneNumberLink />, Monday to Friday 9am to 5.30pm EST.
        </p>
      </ReactBootstrapModal>
    </>
  )
};
